// react
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import { useSetDisplayNameForMeterMeterDisplayNameAnalysisTypeAccountIdMeterIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
// shared-components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFTextField } from '@/components/ui/form';

// components
import type { AnalysisType } from '@/features/energyxplorer/types';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import { toEditMeterDisplayNameFormValues } from './editMeterDisplayNameForm.mappers';
import { createEditMeterDisplayNameFormSchema } from './editMeterDisplayNameForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

interface EditMeterDisplayNameFormProps {
  accountId: number;
  meterId: string;
  analysisType: AnalysisType;
  initialDisplayName?: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export default function EditMeterDisplayNameForm({
  accountId,
  meterId,
  analysisType,
  initialDisplayName,
  onSuccess,
  onCancel,
  onDirtyChange,
}: EditMeterDisplayNameFormProps) {
  const { t } = useTranslation(['forms', 'validation']);
  const dispatch = useDispatch();

  const [setDisplayName, { isLoading }] =
    useSetDisplayNameForMeterMeterDisplayNameAnalysisTypeAccountIdMeterIdPutMutation();

  const schema = createEditMeterDisplayNameFormSchema(t);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditMeterDisplayNameFormValues(initialDisplayName),
    mode: 'onTouched',
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await setDisplayName({
        accountId,
        meterId,
        analysisType,
        displayName: values.displayName,
      }).unwrap();

      dispatch(
        showSnackbar({
          message: t('common.updateSuccess', { ns: 'forms' }),
          severity: 'success',
        }),
      );
      reset(toEditMeterDisplayNameFormValues(values.displayName));
      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({
          message: t('common.updateError', { ns: 'forms' }),
          severity: 'error',
        }),
      );
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <RHFTextField
              name="displayName"
              control={control}
              label={t('editMeterDisplayName.displayName.label', {
                ns: 'forms',
              })}
              placeholder={t('editMeterDisplayName.displayName.placeholder', {
                ns: 'forms',
              })}
              helperText={t('editMeterDisplayName.displayName.helperText', {
                ns: 'forms',
              })}
              required
              fullWidth
            />
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          {onCancel && (
            <CancelButton onClick={onCancel} disabled={isLoading}>
              {t('common.cancel', { ns: 'forms' })}
            </CancelButton>
          )}
          <SubmitButton
            loading={isLoading}
            disabled={!isValid || !isDirty || isLoading}
          >
            {isLoading
              ? t('editMeterDisplayName.submitting', { ns: 'forms' })
              : t('editMeterDisplayName.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
